/**
 * @import {ActionsliderScope, ActionsliderAction} from "./types";
 */
import {asset, RATATOSKR} from "acng/core/service/env.js";
import {sessionStore} from "../api/browser-storage.js";
import angular from "angular";
import templateUrl from "../../html/actionslider.html";
import {parse, stringify} from "@acng/frontend-bounty/std/string.js";

let DEBUG = false;
// DEBUG: DEBUG = true;

angular
  .module('ons-actionslider', ['ngAnimate'])
  .constant('ONSAS_VERSION', '1.8.5')
  .factory('onsActionSocket', onsActionSocket)
  .directive('onsActionslider', onsActionslider)
  ;

onsActionslider.$inject = [];

/**
 * ons-actionslider element.
*
 * @type {import("angular").IDirectiveFactory}
 */
function onsActionslider () {
  return {
    restrict: 'E',
    controller: OnsActionsliderController,
    scope: {
      actionSession: '@',
      userId: '@',
      productId: '@',
      channels: '@',
      language: '@?',
      onAction: '&',
      onScroll: '&',
      onClick: '&'
    },
    templateUrl: asset(templateUrl),
  };
}

OnsActionsliderController.$inject = ['onsActionSocket', '$element', '$scope', '$filter', '$timeout', '$q', '$window', 'ONSAS_VERSION'];

/**
 * Controller for ons-actionslider.
 *
 * A container for all action events.
 *
 * @param {ReturnType<onsActionSocket>} onsActionSocket
 * @param {angular.IRootElementService} $element
 * @param {ActionsliderScope} $scope
 * @param {angular.IFilterService} $filter
 * @param {angular.ITimeoutService} $timeout
 * @param {angular.IQService} $q
 * @param {angular.IWindowService} $window
 * @param {string} ONSAS_VERSION
 */
function OnsActionsliderController (onsActionSocket, $element, $scope, $filter, $timeout, $q, $window, ONSAS_VERSION) {
  var version = sessionStore['onsas-v'] ?? null;
  var $ul = $element.find('ul');
  var sessionId = sessionStore['onsas-i'] ?? null;
  var sessionTimestamp = sessionStore['onsas-t'] ?? Date.now();
  /** @type {ActionsliderAction[]} */
  var internalQueue = sessionStore['onsas-q'] ?? [];
  var channels = [$scope.channels];
  var lastTimestamp = 0;
  var userIdentification = {
    id: $scope.userId,
    productId: $scope.productId
  };

  if (DEBUG) console.debug('actionSlider client version', ONSAS_VERSION);

  $scope.maxActions = 300;
  $scope.lifetime = 7200;
  $scope.currentOffset = 0;
  $scope.actions = sessionStore['onsas-a'] ?? [];
  $scope.updateOffset = updateOffset;

  $window.onbeforeunload = onBeforeUnload($window.onbeforeunload);

  $scope.$watch('language', updateLanguage);

  if (version !== ONSAS_VERSION) {
    sessionTimestamp = Date.now();
    internalQueue = [];
    $scope.actions = [];
  }

  if (sessionId !== $scope.actionSession) {
    sessionTimestamp = Date.now();
    internalQueue = [];
    $scope.actions = [];
  }

  lastTimestamp = $scope.actions.reduce(function (accumulator, action) {
    return action.timestamp > accumulator
      ? action.timestamp
      : accumulator;
  }, lastTimestamp);

  lastTimestamp = internalQueue.reduce(function (accumulator, action) {
    return action.timestamp > accumulator
      ? action.timestamp
      : accumulator;
  }, lastTimestamp);

  if (DEBUG) console.debug('You are ' + $scope.productId + '@' + $scope.userId + ', listening to public channel(s) ' + channels.join(', '));

  onsActionSocket
    .create()
    .connect(userIdentification, channels, lastTimestamp)
    .addOnMessage(onMessage);

  $timeout(processQueue, 0);

  /**
   * @param {"de" | "en"} newLanguage
   * @param {"de" | "en"} oldLanguage
   */
  function updateLanguage(newLanguage, oldLanguage) {
    if (DEBUG) console.debug('updateLanguage', newLanguage, oldLanguage);

    if (newLanguage === oldLanguage) {
      return;
    }

    $scope.$evalAsync(function () {
      $q.when($scope.actions)
        .then(function (actions) {
          return actions.map(function (action) {
            return $scope.onAction({ action: action, language: $scope.language });
          })
        })
        .then(function (actions) {
          return $q.all(actions);
        })
        .then(function (actions) {
          return actions.map(function (action) {
            if(!action) return action;
            action.classes = actionClasses(action);

            return action;
          });
        })
        .then(function (actions) {
          $scope.actions = actions;
        })
        .catch(console.warn)
    });

  }

  /**
   * Called upon an incoming action.
   * @param {ActionsliderAction} action
   */
  function onMessage (action) {
    if (DEBUG) console.debug('onsas new action', action);

    internalQueue.push(action);
  }

  /**
   * Called at regular intervals to add actions from the queue (que) to the actual, visible list of actions.
   */
  function processQueue () {
    $scope.$evalAsync(function () {
      /** @type {ActionsliderAction | undefined} */
      var action;

      for (action = internalQueue.shift(); action && action.timestamp <= sessionTimestamp; action = internalQueue.shift()) {
        addAction(action);
      }

      $timeout(function () {
        $ul.removeClass('onsas-no-anim');

        if (action) {
          addAction(action);

          $scope.currentOffset = 0;
        }

        $scope.actions.forEach(function (action) {
          action.classes = actionClasses(action);
        });

        updateOffset(0);
      }, 666);
    });

    $timeout(processQueue, 1500);
  }

  /** @param {ActionsliderAction} action */
  function addAction (action) {
    $q.when($scope.onAction({ action: action, language: $scope.language }))
      .then(function (displayAction) {
        if (displayAction) {
          displayAction.classes = actionClasses(displayAction);
        }
        return displayAction;
      })
      .then(function (displayAction) {
        if (displayAction) {
          $scope.actions.push(displayAction);

          $scope.actions = $filter('orderBy')($scope.actions, 'timestamp');

          $scope.actions.length > $scope.maxActions && $scope.actions.splice(0, $scope.actions.length - $scope.maxActions);
        }
      })
      .catch(console.warn);
  }

  /**
   * Update the current offset
   *
   * @param {"first" | "last" | number} offsetRelativeToNewestAction
   */
  function updateOffset (offsetRelativeToNewestAction) {
    switch (offsetRelativeToNewestAction) {
      case 'first':
        $scope.currentOffset = $scope.actions.length;
        break;

      case 'last':
        $scope.currentOffset = 0;
        break;

      default:
        $scope.currentOffset += offsetRelativeToNewestAction;
    }

    if ($scope.currentOffset < 0) {
      $scope.currentOffset = 0;
    }

    if ($scope.currentOffset >= $scope.actions.length) {
      $scope.currentOffset = $scope.actions.length - 1;
    }

    if ($scope.currentOffset === 0) {
      $element.addClass('onsas-last-reached');
    } else {
      $element.removeClass('onsas-last-reached');
    }

    if ($scope.currentOffset === $scope.actions.length - 1) {
      $element.addClass('onsas-first-reached');
    } else {
      $element.removeClass('onsas-first-reached');
    }

    $q.when($scope.onScroll({offset: $scope.currentOffset, children: childSizes()}))
      .then(function (css) {
        if (typeof css === 'string' || typeof css === 'object') {
          $ul.css(css);
        }
      })
      .catch(console.warn);
  }

  function childSizes () {
    var children = $ul.children();
    var childSizes = [];
    var childIndex;

    for (childIndex = 0; childIndex < children.length; ++childIndex) {
      childSizes.push({
        width: angular.element(children[childIndex]).width(),
        height: angular.element(children[childIndex]).height()
      });
    }

    return childSizes;
  }

  /**
   * Determine action styles
   *
   * @param {ActionsliderAction} action
   */
  function actionClasses (action) {
    var classes = action.classes || {};

    classes[`onsas-${action.id}`] = true;
    classes['onsas-old'] = action.timestamp + $scope.lifetime * 1000 < Date.now();
    classes['onsas-has-action'] = angular.isDefined(classes.hasAction) ? classes.hasAction : true;

    return classes;
  }

  /** @param {typeof window.onbeforeunload} oldBeforeUnload */
  function onBeforeUnload (oldBeforeUnload) {
    /** @param {BeforeUnloadEvent} ev */
    return function (ev) {
      sessionStore['onsas-i'] = $scope.actionSession;
      sessionStore['onsas-t'] = sessionTimestamp;
      sessionStore['onsas-q'] = internalQueue;
      sessionStore['onsas-a'] = $scope.actions;
      sessionStore['onsas-v'] = ONSAS_VERSION;

      angular.isFunction(oldBeforeUnload) && oldBeforeUnload.apply($window, [ev]);
    };
  }
}

onsActionSocket.$inject = ['$timeout'];

/**
 * Socket factory to re-/connect to ratatoskr.
 *
 * @param {angular.ITimeoutService} $timeout
 */
function onsActionSocket ($timeout) {
  var factory = {};

  factory.create = create;

  return factory;

  function create () {
    /** @type {WebSocket | null} */
    var socket = null;
    var service = {};
    /** @type {((action: ActionsliderAction) => void)[]} */
    var _onMessageHandler = [];
    /** @type {unknown[]} */
    var _messageBuffer = [];
    var _connected = false;
    /** @type {{user: {id: string, productId: string}, channels: unknown, lastAction: number} | null} */
    var _registerData = null;

    service.connect = connect;
    service.addOnMessage = addOnMessage;

    openSocket();

    return service;

    /**
     * @param {{id: string, productId: string}} user
     * @param {unknown} publicChannels
     * @param {number} lastAction
     */
    function connect (user, publicChannels, lastAction) {
      _registerData = {
        user: user,
        channels: publicChannels,
        lastAction: lastAction
      };

      if (DEBUG) console.debug('onsas registrationData', _registerData);

      return sendRegisterData();
    }

    /** @param {(action: ActionsliderAction) => void} func */
    function addOnMessage (func) {
      if (angular.isFunction(func)) {
        _onMessageHandler.push(func);
      }

      return service;
    }

    function onOpen () {
      _connected = true;

      sendBuffered();
      sendRegisterData();
    }

    /** @param {{data: string, timeStamp: number}} action */
    function onMessage (action) {
      /** @type {ActionsliderAction} */
      var data = parse(action.data);

      data.payload = data.payload || {};
      data.timestamp = data.timestamp || action.timeStamp || Date.now();
      _registerData.lastAction = data.timestamp;

      _onMessageHandler.forEach(function (handler) {
        handler(data);
      });
    }

    function onClose () {
      _connected = false;

      $timeout(openSocket, 3333);
    }

    function openSocket () {
      const url = new URL(RATATOSKR);

      url.protocol = 'wss';
      url.pathname = '/as/websocket';
      socket = new WebSocket(url);

      socket.onopen = onOpen;
      socket.onclose = onClose;
      socket.onmessage = onMessage;
    }

    function sendRegisterData () {
      _connected && _registerData !== null && send(false)(_registerData);

      return service;
    }

    /** @param {boolean} buffer */
    function send (buffer) {
      /** @param {import("@acng/frontend-bounty/std/string.js").JSON} data */
      return function (data) {
        if (_connected) {
          socket?.send(stringify(data));
        } else if (buffer !== false) {
          _messageBuffer.push(data);
        }
      };
    }

    function sendBuffered () {
      var messageBufferClone = JSON.parse(JSON.stringify(_messageBuffer));

      _messageBuffer = [];

      messageBufferClone.forEach(send(true));
    }
  }
}
